/**
 * FileUpload.js
 * Created by Ajit on 09/20/2021.
 */

import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {labels} from '../../../util/utils';
import {setIsChangeReasonRequired} from '../../../actions/Document/fourFortyGlDocumentActions';
import {uploadDocumentWithDocumentHelper2} from '../../../actions/Task/documenthubactions';
import {raiseDochelperError, raiseJSValidationError} from '../../../actions/erroractions';
import Utility from '../../../util/utilityfunctions';
import {useAresUrlParameters} from '../../../util/Ares/customHooks';
import {DragDropFileUpload} from '@ey/canvascoreservices';
import {
	dragAndDropUploadComplete,
	dragAndDropUploadProgress,
	dragAndDropUploadTasksComplete,
	startUpchunkUpload
} from '../../../actions/Upload/uploadaction';
import {addOverWriteDocument} from '../../../actions/Document/duplicatedocumentsactions';
import {updateDocument} from '../../../actions/Document/documentactions';
import * as DocumentExtensionActions from '../../../actions/Document/documentextensionactions';
import {
	createDocumentChangeComment,
	updateDocumentChangeComment
} from '../../../actions/Document/documentextensionactions';
import {documentTypes, droppedDocumentCount} from '../../../util/uiconstants';
import NoDataPlaceholder from '../ResourceWithLink/NoDataPlaceholder';
import {SectionIdContext} from '../CanvasForm/Common/Context';
import {useUserPreferences} from '../../../util/customHooks';
import {loadStorage, saveStorage, clearItem} from '../localStorage';
import {clearUploadDocumentIds, clearUploadDocuments} from '../../../actions/Document/bulkRelateActions';

let uploadedFiles = [];

function FileUpload(
	{
		children,
		bodyId,
		entityType,
		documentType,
		overrideClassName,
		dragDropEnabled,
		requireValidations = false,
		payloadRef,
		relatedTasks,
		maxDropzoneFiles = 10,
		parentName,
		signoffRequirements,
		documentUploadComplete,
		bodyTypeId,
		showCrashDocumentWarningMessage = false,
		isDisabled
	},
	ref
) {
	const [uploadBrowserOpen, setUploadBrowserOpen] = useState(false);

	//const parentName = parentName;

	const engagement = useSelector((state) => state.engagement);
	const quickLink = useSelector((state) => state.quickLink);
	const documentExtensions = useSelector((state) => state.documentExtensions);
	const taskDetails = useSelector((state) => state.taskDetails);
	const userPreferences = useUserPreferences();

	const isChangeReasonRequired = Utility.isChangeReasonRequired(
		engagement,
		quickLink,
		undefined
	);

	const progressRef = useRef([]);
	const uploadRef = useRef([]);

	const dispatch = useDispatch();

	useImperativeHandle(ref, () => {
		return {
			onUploadClick: handleOnClick
		};
	});

	const aresUrlParameters = useAresUrlParameters();

	const contextSectionId = useContext(SectionIdContext);

	const sectionId = useMemo(() => contextSectionId || aresUrlParameters.sectionId, [
		contextSectionId,
		aresUrlParameters
	]);

	const taskId = useMemo(() => aresUrlParameters.taskId, [aresUrlParameters]);

	function processDocumentUpload(engagement) {
		if (uploadBrowserOpen) {
			return;
		}

		try {
			dispatch(clearUploadDocumentIds());
			dispatch(clearUploadDocuments());
			setUploadBrowserOpen(true);
			dispatch(
				uploadDocumentWithDocumentHelper2(
					entityType,
					parentName,
					engagement.engagementId,
					engagement.workspaceId,
					taskId,
					documentType,
					undefined,
					undefined,
					undefined,
					false,
					taskDetails?.taskTypeId,
					{
						connectionid: window.jQuery.connection.documentHubV2.connection.id
					},
					bodyId,
					requireValidations,
					sectionId,
					relatedTasks,
					showCrashDocumentWarningMessage,
					signoffRequirements,
					bodyTypeId
				)
			).then(() => {
				setUploadBrowserOpen(false);
			});
		} catch (e) {
			setUploadBrowserOpen(false);
			// eslint-disable-next-line no-console
			console.log('No Connection to DocHelper Available');
			dispatch(raiseDochelperError(['0010', 'Connection Failed', labels.documentHelperConnectionIssue]));
		}
	}

	function handleOnClick() {
		if (engagement) {
			// 1. Check if the upload is after the report release date:
			let showModal = isChangeReasonRequired;
			// If the upload happens before the report release date, upload the file
			if (showModal === true) {
				dispatch(setIsChangeReasonRequired(showModal, showModal, bodyId));
			} else {
				processDocumentUpload(engagement);
			}
		}
	}

	const handleOnOverwrite = useCallback(
		(document) => {
			const blob = progressRef.current.find((value) => value.name === document.documentName);
			if (!document.isOverWrite) {
				/*user chose to not overwrite. just remove the file from the list*/
				progressRef.current = progressRef.current.filter((value) => value.name !== document.documentName);

				const count = loadStorage(droppedDocumentCount);

				if (count >= 1) {
					saveStorage(droppedDocumentCount, count - 1);
				}

				upload(blob, document, false, true);
			} else {
				upload(blob, document, true);
			}
		},
		[upload]
	);

	const handleOnDuplicate = useCallback(
		(fileId, file, document, onOverwrite) => {
			return addOverWriteDocument({
				engagementId: engagement?.engagementId,
				userId: userPreferences.id,
				parentName,
				documentId: document.Id || document.id,
				documentName: file?.path ? file.path : document.OriginalFileName,
				documentTypeID: document.DocumentTypeID,
				payload: {
					id: fileId,
					parentName,
					bodyId
				},
				onOverwrite: onOverwrite
			});
		},
		[engagement?.engagementId, userPreferences.id, parentName, bodyId]
	);

	const handleOnComplete = useCallback(
		(documentId, file) => {
			let files = uploadedFiles;
			files.push({file: progressRef.current.find((value) => value.name === file.name), documentId: documentId});
			uploadedFiles = files;

			const afterUpload = () => {
				const count = loadStorage(droppedDocumentCount);

				if (count === 1) {
					clearItem(droppedDocumentCount);
					documentUploadComplete && documentUploadComplete(documentId);
				} else {
					saveStorage(droppedDocumentCount, count - 1);
				}
			};

			dispatch(dragAndDropUploadProgress(file.name, documentId, 85)).then(() => {
				if (isChangeReasonRequired) {
					dispatch(
						updateDocument(engagement?.engagementId, documentId, {
							documentChangeTypeId: payloadRef.current.documentChangeTypeId
						})
					);

					const getDocumentExtensionsAction = DocumentExtensionActions.getAllDocumentExtensions(
						engagement?.engagementId,
						documentId
					);

					// 1. Search for documentExtension data for the uploaded document
					// 2.1. If nothing was found, send a createDocExtension action
					// 2.2. If there is data for that docId, send a updateDocExtension action
					dispatch(getDocumentExtensionsAction);

					afterUpload();
				} else {
					dispatch(dragAndDropUploadTasksComplete(file.name, documentId)).then(() => {
						afterUpload();
					});
				}
			});
		},
		[dispatch, documentUploadComplete, engagement?.engagementId, isChangeReasonRequired, payloadRef]
	);

	useEffect(() => {
		if (documentExtensions && documentExtensions.length > 0 && uploadedFiles.length > 0) {
			const changeReason = payloadRef.current?.documentChangeComment;
			if (documentExtensions[0].documentId) {
				const file = uploadedFiles.find((f) => f.documentId === documentExtensions[0].documentId);
				uploadedFiles = uploadedFiles.filter((f) => f.documentId !== documentExtensions[0].documentId);
				dispatch(
					updateDocumentChangeComment(
						engagement?.engagementId,
						documentExtensions[0].documentId,
						documentExtensions[0].id,
						{
							changeReason: changeReason
						}
					)
				).then(() => {
					if (file) {
						progressRef.current = progressRef.current.filter((f) => f.name !== file.file?.name);
						dispatch(dragAndDropUploadComplete(file.file.name, documentExtensions[0].documentId)).then(() => {
							dispatch(dragAndDropUploadTasksComplete(file.file.name, documentExtensions[0].documentId));
						});
					}
				});
			} else {
				const file = uploadedFiles.shift();
				dispatch(
					createDocumentChangeComment(engagement?.engagementId, file?.documentId, {
						changeReason: changeReason
					})
				).then(() => {
					dispatch(dragAndDropUploadComplete(file?.name, file?.documentId)).then(() => {
						dispatch(dragAndDropUploadTasksComplete(file?.name, file?.documentId));
					});
				});
			}
		}
	}, [documentExtensions]);

	const handleOnDrop = useCallback(() => {
		while (uploadRef.current.length > 0) {
			const blob = uploadRef.current.pop();
			progressRef.current.push(blob);

			upload && upload(blob);
		}
	}, [upload]);

	const onDrop = useCallback(
		(blobs) => {
			saveStorage(droppedDocumentCount, blobs.length);
			if (blobs.length > maxDropzoneFiles) {
				dispatch(raiseJSValidationError(labels.maxUploadFilesError));
				return;
			}
			uploadRef.current = blobs;
			handleOnDrop();
		},
		[dispatch, handleOnDrop, maxDropzoneFiles]
	);

	const upload = useCallback(
		(blob, document = undefined, overwrite = false, discard = false) => {
			let showModal = isChangeReasonRequired && !overwrite && !discard;
			// If the upload happens before the report release date, upload the file
			if (showModal === true) {
				Utility.setDragAndDropUploadObject({
					blob,
					document,
					overwrite,
					discard,
					handleOnDuplicate,
					handleOnComplete,
					handleOnOverwrite,
					relatedTasks,
					signoffRequirements
				});
				dispatch(setIsChangeReasonRequired(showModal, showModal, bodyId));
			} else {
				dispatch(
					startUpchunkUpload(
						userPreferences.id, // userId
						engagement.engagementId, // engagementId
						parentName, // parentName
						bodyId, // entityUid
						document?.payload.id, // documentId
						blob, // document blob
						documentTypes.CRASH, // document type id
						17, // document source id, default to doc helper
						overwrite, // overwrite flag
						discard, // discard flag
						isChangeReasonRequired,
						sectionId,
						handleOnDuplicate, // callback
						handleOnComplete, // callback
						handleOnOverwrite, // callback
						relatedTasks,
						showCrashDocumentWarningMessage,
						requireValidations,
						signoffRequirements,
						bodyTypeId
					)
				);
			}
		},
		[
			isChangeReasonRequired,
			handleOnDuplicate,
			handleOnComplete,
			handleOnOverwrite,
			relatedTasks,
			signoffRequirements,
			dispatch,
			bodyId,
			userPreferences.id,
			engagement.engagementId,
			parentName,
			sectionId,
			requireValidations,
			bodyTypeId,
			showCrashDocumentWarningMessage
		]
	);

	const dragDropLabels = {
		documentHelperConnectionIssue: labels.documentHelperConnectionIssue,
		placeholderText: ''
	};

	const addEvidenceLink = () => {
		return (
			<NoDataPlaceholder
				placeholder={labels.dragDropUploadPlaceholder}
				onPlaceholderClickConfig={{
					addDocument: handleOnClick
				}}
			/>
		);
	};

	return (
		<StyledFileUpload className="StyledFileUpload">
			<section className={`${overrideClassName ? overrideClassName : 'drag-drop-file-container'}`}>
				{dragDropEnabled && !isDisabled ? (
					<DragDropFileUpload
						showPlaceholder
						labels={dragDropLabels}
						placeholderComponent={addEvidenceLink()}
						onDropDocument={onDrop}
					>
						{children}
					</DragDropFileUpload>
				) : (
					children
				)}
			</section>
		</StyledFileUpload>
	);
}

export default forwardRef(FileUpload);

const StyledFileUpload = styled.section`
	.drag-drop-file-container {
		width: 100%;
		text-align: center;
		background: var(--neutrals-100);
		.drag-drop-title {
			border: var(--px-2) dashed var(--neutrals-300);
			padding: var(--px-20);
			text-align: center;
			width: 100%;

			svg {
				margin: 0 auto;
				display: block;
				fill: var(--neutrals-700);
			}
		}
	}
	button.upload-btn {
		font-weight: bold;
		color: var(--blue-600);
		padding-left: 0;
	}
	.upload-link {
		cursor: pointer;
	}
	.document-list-dropzone-placeholder {
		min-height: var(--px-200) !important;
		padding: var(--px-24) !important;
		border: 0 !important;
		border-radius: 0 0 var(--px-12) var(--px-12) !important;
		background: var(--neutrals-00white) !important;
		.document-list-dropzone-placeholder-inner {
			border-radius: var(--px-12) var(--px-12) var(--px-12) var(--px-12) !important;
			svg {
				width: var(--px-44);
				height: var(--px-32);
				max-width: var(--px-44);
				max-height: var(--px-32);
				fill: var(--neutrals-1000);
				margin-top: var(--px-18);
				cursor: default;
			}
			div {
				display: flex;
				margin-top: var(--px-10);
				margin-bottom: var(--px-18);
				min-height: var(--px-22);
				font-style: normal;
				font-weight: 400;
				font-size: var(--px-14);
				line-height: var(--px-22);
				color: var(--neutrals-900);
				.createRichTextElementSpanWrapper {
					font-weight: 300;
					font-size: var(--px-14);
					line-height: var(--px-22);
				}
				a {
					font-weight: 700;
					font-size: var(--px-14);
					line-height: var(--px-22);
				}
			}
		}
	}
`;
